// Swappable font-group presets. Each group is named after the theme its font
// combination originally came from, but can be applied to any theme.
//
// bodyEmphasis / button / bioItalic are additive keys introduced for the
// "combo" group. Existing groups map them onto their current body font with
// no forced weight, so referencing these keys never changes the four
// original presets' appearance.
//
// displayTitle / reviewerName / pairValue / pairCaption / statCaption /
// verified / priceValue / itemDescription are additional roles covering
// elements whose original per-theme markup used a DIFFERENT generic role
// name (label/body/name/socialStat) for the *same kind* of element in each
// theme. Every value below is that theme's own pre-existing literal for that
// exact element, so referencing these roles does not change any theme's own
// default rendering — it only makes cross-theme font-group application
// consistent (e.g. applying "Edge" to another theme's "Verified" badge now
// resolves to Edge's own Verified-badge font, not whatever unrelated role
// that theme's file happened to reuse).
export const FONT_GROUPS = {
  standard: {
    heading: 'font-outfit',
    name: 'font-noto',
    socialStat: 'font-notodisplay',
    label: 'font-noto',
    labelAlt: 'font-noto',
    body: 'font-outfit',
    bodyEmphasis: 'font-outfit',
    button: 'font-outfit',
    bioItalic: 'font-outfit',
    displayTitle: 'font-notodisplay',
    reviewerName: 'font-notodisplay',
    pairValue: 'font-noto',
    pairCaption: 'font-outfit',
    statCaption: 'font-outfit',
    verified: 'font-outfit',
    priceValue: 'font-noto',
    itemDescription: 'font-noto'
  },
  dark: {
    heading: 'font-roboto',
    name: 'font-antonio',
    socialStat: 'font-antonio',
    label: 'font-outfit',
    labelAlt: 'font-outfit',
    body: 'font-roboto',
    bodyEmphasis: 'font-roboto',
    button: 'font-roboto',
    bioItalic: 'font-roboto',
    displayTitle: 'font-antonio',
    reviewerName: 'font-antonio',
    pairValue: 'font-roboto',
    pairCaption: 'font-roboto',
    statCaption: 'font-outfit',
    verified: 'font-outfit',
    priceValue: 'font-antonio',
    itemDescription: 'font-roboto'
  },
  natural: {
    heading: 'font-nunitosans',
    name: 'font-mollie',
    socialStat: 'font-nunitosans',
    label: 'font-nunitosans',
    labelAlt: 'font-nunito',
    body: 'font-nunito',
    bodyEmphasis: 'font-nunito',
    button: 'font-nunito',
    bioItalic: 'font-nunito',
    displayTitle: 'font-nunito',
    reviewerName: 'font-mollie',
    pairValue: 'font-nunito',
    pairCaption: 'font-nunito',
    statCaption: 'font-nunitosans',
    verified: 'font-nunito',
    priceValue: 'font-nunito',
    itemDescription: 'font-nunito'
  },
  classic: {
    heading: 'font-roboto',
    name: 'font-roboto',
    socialStat: 'font-notodisplay',
    label: 'font-outfit',
    labelAlt: 'font-outfit',
    body: 'font-roboto',
    bodyEmphasis: 'font-roboto',
    button: 'font-roboto',
    bioItalic: 'font-roboto',
    displayTitle: 'font-roboto',
    reviewerName: 'font-roboto',
    pairValue: 'font-roboto',
    pairCaption: 'font-roboto',
    statCaption: 'font-outfit',
    verified: 'font-outfit',
    priceValue: 'font-roboto',
    itemDescription: 'font-roboto'
  },
  // New 5th combination: Syne / Work Sans / Fraunces Italic / Space Mono.
  // Weight/style is baked in with `!` since each semantic slot maps to a
  // single fixed weight per spec, regardless of whatever weight class a
  // given usage site already applies for the other four presets.
  combo: {
    heading: 'font-syne !font-semibold',           // section titles — Syne 600
    name: 'font-syne !font-bold',                  // creator's name — Syne 700
    socialStat: 'font-syne !font-bold',             // large stats/percentages — Syne 700
    label: 'font-spacemono !font-normal',           // labels — Space Mono 400
    labelAlt: 'font-spacemono !font-normal',        // labels — Space Mono 400
    body: 'font-worksans !font-normal',             // ordinary values/text — Work Sans 400
    bodyEmphasis: 'font-worksans !font-medium',     // content titles, names, links, important values — Work Sans 500
    button: 'font-worksans !font-semibold',         // buttons/CTAs — Work Sans 600
    bioItalic: 'font-fraunces !font-normal italic', // bio paragraph + second content line — Fraunces italic 400
    displayTitle: 'font-worksans !font-medium',     // deliverable/rate/link titles — Work Sans 500
    reviewerName: 'font-syne !font-bold',           // review card name — Syne 700, matches creator name
    pairValue: 'font-worksans !font-medium',        // age/location values — Work Sans 500
    pairCaption: 'font-spacemono !font-normal',     // age/location/rating captions — Space Mono 400
    statCaption: 'font-spacemono !font-normal',     // stat captions — Space Mono 400
    verified: 'font-spacemono !font-normal',        // "Verified" badge — Space Mono 400
    priceValue: 'font-worksans !font-medium',       // rate prices — Work Sans 500
    itemDescription: 'font-worksans !font-normal'   // deliverable descriptions — Work Sans 400
  }
};

export const FONT_GROUP_KEYS = ['standard', 'dark', 'natural', 'classic', 'combo'];

export const FONT_GROUP_LABELS = {
  standard: 'Pure',
  dark: 'Edge',
  natural: 'Noir',
  classic: 'Arc',
  combo: 'Combo'
};
